import { useContext, useState } from "react";

import isContainer from "is-container";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import useYupValidation from "../../../../../../hooks/useYupValidation";
import { Waybill } from "../../../../../../models/waybill";
import { ShipmentsContext } from "../context/useContext";

export const useShipmentContext = () => {
  const context = useContext(ShipmentsContext);
  const { t: translate } = useTranslation();

  const [errors, setErrors] = useState<yup.ValidationError[]>();
  const { fieldHasError, getErrorByField } = useYupValidation(errors || []);

  const schema = yup.object().shape({
    isSubscribed: yup.boolean(),
    shipmentType: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    loadType: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    carrierScac: yup.string().when("isSubscribed", {
      is: true,
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
    }),
    jobs: yup
      .array()
      .of(yup.object().nullable())
      .when("isSubscribed", {
        is: true,
        then: yup
          .array()
          .min(1)
          .of(yup.object().required("thisFieldIsRequired"))
          .test("is-required", translate("thisFieldIsRequired"), function(value) {
            if (value && value.length) return true;

            NotificationToast.error(translate("addOrderToShipment"), {
              id: "add-order-to-shipment",
            });

            return false;
          }),
      }),
    containers: yup
      .array()
      .min(1)
      .of(
        yup.object().shape({
          container: yup
            .string()
            .required(translate("thisFieldIsRequired"))
            .test("is-container", translate("pleaseAddAValidContainerNumber"), function(value) {
              if (value && !isContainer(value))
                return this.createError({ message: translate("pleaseAddAValidContainerNumber") });

              if (value) return true;

              return false;
            }),
          size: yup.string().notRequired(),
          seal: yup.string().notRequired(),
          grossWeight: yup.string().notRequired(),
          netWeight: yup.number().notRequired(),
          tareWeight: yup.number().notRequired(),
          volumetricWeight: yup.number().notRequired(),
        })
      ),
    billOfLanding: yup.string().when("isSubscribed", {
      is: true,
      then: yup.string().when("carrierScac", {
        is: (carrierScac: string) => carrierScac?.trim().length,
        then: yup.string().required(translate("thisFieldIsRequired")),
        otherwise: yup.string().notRequired(),
      }),
      otherwise: yup.string().notRequired(),
    }),
    bookingNo: yup.string().when("billOfLanding", {
      is: (billOfLanding: string) => !billOfLanding?.trim().length,
      then: yup.string().when("isSubscribed", {
        is: true,
        then: yup.string().required(translate("thisFieldIsRequired")),
        otherwise: yup.string().notRequired(),
      }),
      otherwise: yup.string().notRequired(),
    }),
  });

  const onValidate = (shipment?: Waybill, isSubscribed?: boolean) => {
    try {
      schema.validateSync(
        {
          isSubscribed,
          carrierScac: shipment?.carrierScac,
          shipmentType: shipment?.shipmentType,
          loadType: shipment?.loadType,
          jobs: shipment?.jobs,
          containers: shipment?.containers,
          billOfLanding: shipment?.billOfLanding,
          bookingNo: shipment?.bookingNo,
        },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (error: any) {
      if (error instanceof yup.ValidationError) {
        setErrors(error.inner);
      }

      return false;
    }
  };



  return {
    ...context,
    errors,
    fieldHasError,
    getErrorByField,
    onValidate,
  };
};
